import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import LandingFigure, { ScreenshotGallery } from '../components/LandingFigure';
import { getFeature, SITE_LAST_UPDATED } from '../data/features';
import { longDate } from '../lib/siteDates';
import {
  Check, ArrowRight, Focus, Clock, LayoutDashboard, Link as LinkIcon,
  Users, Calendar, Sparkles, FileText, BarChart3, Webhook, Video,
  Coffee, Mail, KanbanSquare, ChevronRight
} from 'lucide-react';

const ICON_MAP = {
  Focus, Clock, LayoutDashboard, Link: LinkIcon, Users, Calendar, Sparkles,
  FileText, BarChart3, Webhook, Video, Coffee, Mail, KanbanSquare,
};

const COLOR_CLASSES = {
  blue:   { pill: 'from-blue-500/20 to-blue-600/20 border-blue-500/30 text-blue-400',     accent: 'text-blue-400',  ring: 'ring-blue-500/40' },
  yellow: { pill: 'from-amber-500/20 to-amber-600/20 border-amber-500/30 text-amber-400', accent: 'text-amber-400', ring: 'ring-amber-500/40' },
  pink:   { pill: 'from-pink-500/20 to-pink-600/20 border-pink-500/30 text-pink-400',     accent: 'text-pink-400',  ring: 'ring-pink-500/40' },
  purple: { pill: 'from-purple-500/20 to-purple-600/20 border-purple-500/30 text-purple-400', accent: 'text-purple-400', ring: 'ring-purple-500/40' },
  green:  { pill: 'from-green-500/20 to-green-600/20 border-green-500/30 text-green-400', accent: 'text-green-400', ring: 'ring-green-500/40' },
};

const FeaturePage = () => {
  const { t, i18n } = useTranslation(['landing', 'features']);
  const { featureId } = useParams();
  const feature = getFeature(featureId, t) || getFeature('task-management', t);
  const Icon = ICON_MAP[feature.icon] || LayoutDashboard;
  const colors = COLOR_CLASSES[feature.color] || COLOR_CLASSES.yellow;
  const heroShot = feature.screenshots?.[0];
  const updated = longDate(SITE_LAST_UPDATED, i18n.resolvedLanguage || 'en');

  const related = (feature.relatedFeatures || []).map((id) => getFeature(id, t)).filter((f) => f?.title);

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <SEO
        title={feature.seoTitle || feature.title}
        description={feature.seoDescription}
        keywords={feature.keywords}
        canonical={`/features/${featureId || 'task-management'}`}
        image={heroShot?.src}
        faqItems={feature.extendedFaq}
        howTo={feature.howItWorks}
        softwareApp={{
          name: `BusyBee.Day - ${feature.title}`,
          applicationSubCategory: feature.title,
          description: feature.seoDescription,
          screenshot: heroShot?.src,
          featureList: feature.benefits,
          url: `https://busybee.day/features/${featureId}`,
        }}
        breadcrumbs={[
          { name: t('pricing.breadcrumb_home'), url: '/' },
          { name: t('feature_page.features_breadcrumb'), url: '/features/task-management' },
          { name: feature.title }
        ]}
      />
      <Navbar />

      <main className="pt-12 pb-24">
        <ScreenshotGallery items={feature.screenshots || []}>
        <article itemScope itemType="https://schema.org/SoftwareApplication">
        {/* Hero - answer-engine-friendly opener */}
        <section className="max-w-7xl mx-auto px-4 pt-12 pb-12 sm:pt-16">
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
            <Link to="/" className="hover:text-white transition-colors">{t('pricing.breadcrumb_home')}</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/features/task-management" className="hover:text-white transition-colors">{t('feature_page.features_breadcrumb')}</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-gray-300">{feature.title}</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-6">
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r ${colors.pill} border mb-6 text-xs font-medium`}>
                <Icon className="w-3.5 h-3.5" />
                <span>{t('feature_page.badge')}</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-5 leading-[1.05]">
                {feature.title}
              </h1>
              <p className="text-xl text-gray-300 mb-5">{feature.subtitle}</p>
              <p className="text-base text-gray-400 leading-relaxed mb-8 max-w-2xl">
                {feature.summary}
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                <Link to="/register" className="bg-amber-500 text-black px-6 py-3 rounded-lg font-semibold hover:bg-amber-400 transition-colors inline-flex items-center gap-2">
                  {t('feature_page.cta_start_free')}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/pricing" className="border border-gray-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-white/5 transition-colors">
                  {t('feature_page.cta_see_pricing')}
                </Link>
              </div>
              {/* No star rating: there is no review source behind one, and fabricated
                  ratings are banned by Annex I of Directive 2005/29/EC. */}
              <div className="flex items-center gap-6 text-sm text-gray-500">
                <span>{t('feature_page.last_updated')} {updated}</span>
              </div>
            </div>

            {heroShot && (
              <div className="lg:col-span-6">
                <LandingFigure
                  src={heroShot.src}
                  alt={heroShot.alt}
                  caption={heroShot.caption}
                  eager
                  frame="hero"
                  ring={colors.ring}
                />
              </div>
            )}
          </div>
        </section>

        {/* What is - short, scannable AEO answer */}
        {feature.whatIs && (
          <section className="max-w-4xl mx-auto px-4 py-12">
            <div className="grid md:grid-cols-3 gap-8 items-start">
              <div className="md:col-span-1">
                <div className="text-xs uppercase tracking-wider text-amber-400 font-semibold mb-2">{t('feature_page.section.quick_answer')}</div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white">{t('feature_page.what_is_question', { name: feature.title.toLowerCase() })}</h2>
              </div>
              <div className="md:col-span-2">
                <p className="text-lg text-gray-300 leading-relaxed">{feature.whatIs}</p>
              </div>
            </div>
          </section>
        )}

        {/* How it works (HowTo) */}
        {feature.howItWorks?.steps?.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 py-16">
            <div className="max-w-3xl mb-10">
              <div className="text-xs uppercase tracking-wider text-amber-400 font-semibold mb-2">{t('feature_page.section.how_it_works')}</div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white">{feature.howItWorks.name}</h2>
              <p className="text-gray-400 mt-3">{feature.howItWorks.description}</p>
            </div>
            <ol className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {feature.howItWorks.steps.map((step, i) => (
                <li key={i} className="bg-white/5 border border-gray-800 rounded-2xl p-6 hover:border-gray-700 transition-colors">
                  <div className={`w-9 h-9 rounded-full bg-gradient-to-r ${colors.pill} border flex items-center justify-center text-sm font-bold mb-4`}>
                    {i + 1}
                  </div>
                  <h3 className="text-white font-semibold mb-2">{step.name}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{step.text}</p>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Screenshots gallery (skip first which is in hero) */}
        {feature.screenshots && feature.screenshots.length > 1 && (
          <section className="max-w-7xl mx-auto px-4 py-16">
            <div className="max-w-3xl mb-10">
              <div className="text-xs uppercase tracking-wider text-amber-400 font-semibold mb-2">{t('feature_page.section.screenshots')}</div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white">{t('feature_page.screenshots_headline', { name: feature.title.toLowerCase() })}</h2>
            </div>
            {(() => {
              const extras = feature.screenshots.slice(1);
              const isSingle = extras.length === 1;
              return (
                <div className={isSingle ? 'max-w-4xl mx-auto' : 'grid md:grid-cols-2 gap-6'}>
                  {extras.map((s, i) => (
                    <LandingFigure key={i} src={s.src} alt={s.alt} caption={s.caption} />
                  ))}
                </div>
              );
            })()}
          </section>
        )}

        {/* Use cases */}
        {feature.useCases?.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 py-16">
            <div className="max-w-3xl mb-10">
              <div className="text-xs uppercase tracking-wider text-amber-400 font-semibold mb-2">{t('feature_page.section.use_cases')}</div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white">{t('feature_page.use_cases_headline', { name: feature.title.toLowerCase() })}</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {feature.useCases.map((uc, i) => (
                <div key={i} className="bg-white/5 border border-gray-800 rounded-2xl p-6 hover:border-gray-700 transition-colors">
                  <h3 className={`font-semibold mb-2 ${colors.accent}`}>{uc.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{uc.text}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* All benefits */}
        <section className="max-w-4xl mx-auto px-4 py-16">
          <div className="mb-10">
            <div className="text-xs uppercase tracking-wider text-amber-400 font-semibold mb-2">{t('feature_page.section.everything_included')}</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">{t('feature_page.capabilities_headline')}</h2>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3">
            {feature.benefits.map((benefit, i) => (
              <li key={i} className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-gray-800">
                <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-amber-400" />
                </div>
                <span className="text-gray-300 text-sm leading-relaxed">{benefit}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Comparison */}
        {feature.comparison && (
          <section className="max-w-7xl mx-auto px-4 py-16">
            <div className="max-w-3xl mb-10">
              <div className="text-xs uppercase tracking-wider text-amber-400 font-semibold mb-2">{t('feature_page.section.comparison')}</div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white">{feature.comparison.title}</h2>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-gray-800 bg-white/[0.02]">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="border-b border-gray-800 bg-white/[0.03]">
                    {feature.comparison.columns.map((col, i) => (
                      <th key={i} className={`px-5 py-4 text-left font-semibold ${i === 1 ? 'text-amber-400' : 'text-gray-300'}`}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {feature.comparison.rows.map((row, i) => (
                    <tr key={i} className="border-b border-gray-800 last:border-0">
                      {row.map((cell, j) => (
                        <td key={j} className={`px-5 py-4 ${j === 0 ? 'text-gray-300 font-medium' : j === 1 ? 'text-amber-300' : 'text-gray-400'}`}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              {t('feature_page.comparison_disclaimer', { date: updated })}
            </p>
          </section>
        )}

        {/* FAQ - AEO-optimized Q&A */}
        {feature.extendedFaq?.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 py-16">
            <div className="mb-10">
              <div className="text-xs uppercase tracking-wider text-amber-400 font-semibold mb-2">{t('feature_page.section.faq')}</div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white">{t('feature_page.faq_headline')}</h2>
            </div>
            <div className="space-y-3">
              {feature.extendedFaq.map((item, i) => (
                <details key={i} className="group bg-white/5 border border-gray-800 rounded-xl overflow-hidden hover:border-gray-700 transition-colors">
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-4 p-5">
                    <h3 className="text-white font-semibold text-base sm:text-lg">{item.question}</h3>
                    <ChevronRight className="w-5 h-5 text-gray-500 group-open:rotate-90 transition-transform flex-shrink-0 mt-0.5" />
                  </summary>
                  <div className="px-5 pb-5 text-gray-400 leading-relaxed text-sm sm:text-base">
                    {item.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Author byline / E-E-A-T */}
        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="flex items-center gap-4 p-5 bg-white/[0.03] border border-gray-800 rounded-2xl">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-black font-bold text-lg">
              🐝
            </div>
            <div className="flex-1">
              <div className="text-white font-semibold">{t('feature_page.byline.title')}</div>
              <div className="text-sm text-gray-500">
                {t('feature_page.byline.body', { date: updated })}
              </div>
            </div>
          </div>
        </section>

        {/* Related features */}
        {related.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 py-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8 text-center">{t('feature_page.related_features_headline')}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {related.map((f) => {
                const FIcon = ICON_MAP[f.icon] || LayoutDashboard;
                return (
                  <Link
                    key={f.id}
                    to={`/features/${f.id}`}
                    className="p-5 bg-white/5 rounded-xl border border-gray-800 hover:border-amber-500/30 transition-colors group"
                  >
                    <FIcon className="w-6 h-6 text-amber-400 mb-3" />
                    <h3 className="text-white font-semibold mb-1 group-hover:text-amber-400 transition-colors">{f.title}</h3>
                    <p className="text-gray-500 text-sm line-clamp-2">{f.subtitle}</p>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Final CTA */}
        <section className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t('feature_page.final_cta.headline', { name: feature.title })}</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            {t('feature_page.final_cta.body')}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/register" className="bg-amber-500 text-black px-8 py-4 rounded-xl font-semibold hover:bg-amber-400 transition-colors inline-flex items-center gap-2">
              {t('feature_page.cta_start_free')}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/pricing" className="border border-gray-700 text-white px-8 py-4 rounded-xl font-medium hover:bg-white/5 transition-colors">
              {t('feature_page.cta_see_pricing')}
            </Link>
          </div>
        </section>
        </article>
        </ScreenshotGallery>
      </main>

      <Footer />
    </div>
  );
};

export default FeaturePage;
